/**
 * API клиент для MAX Client (go-max-client)
 */
import { apiClient } from './client';

export interface QRAuthResult {
  qr_code_url: string;
  qr_code_data: string;
  expires_at: string;
}

export interface AuthStatus {
  status: 'connected' | 'disconnected' | 'auth_required' | 'scanning';
  phone?: string;
  user_id?: number;
  messages_count?: number;
}

export interface SendCodeResult {
  phone: string;
  expires_in_seconds: number;
  request_count_left?: number;
}

export interface VerifyCodeResult {
  status: 'connected';
  phone?: string;
  user_id?: number;
}

export interface PhoneAuthError {
  type: 'captcha_required' | 'flood_wait' | 'invalid_code' | 'code_expired' | 'invalid_phone' | 'auth_error';
  message: string;
}

export interface UserInfo {
  phone?: string;
  found: boolean;
  user_id?: number;
  chat_id?: number;
  first_name?: string;
  last_name?: string;
  reason?: string;
}

export async function startQRAuth(tenantId?: string): Promise<QRAuthResult> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  const response = await apiClient.post<QRAuthResult>('/max-client/qr-auth', {}, { headers, params });
  return response.data;
}

export async function checkAuthStatus(tenantId?: string): Promise<AuthStatus> {
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  const response = await apiClient.get<AuthStatus>('/max-client/status', { params });
  return response.data;
}

export async function disconnect(tenantId?: string): Promise<void> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  await apiClient.post('/max-client/disconnect', {}, { headers, params });
}

/** Поиск пользователя MAX по телефону (go-max-client phone-search). */
export async function searchByPhone(phone: string, tenantId?: string): Promise<UserInfo> {
  const params: Record<string, string> = { phone };
  if (tenantId) params.tenant_id = tenantId;
  const response = await apiClient.get<UserInfo>('/max-client/search', { params });
  return response.data;
}

/** Проверка числового MAX chat ID (без @username). */
export async function searchByChatId(chatId: string, tenantId?: string): Promise<UserInfo> {
  const params: Record<string, string> = { chat_id: chatId.replace(/^max:/i, '') };
  if (tenantId) params.tenant_id = tenantId;
  const response = await apiClient.get<UserInfo>('/max-client/user', { params });
  return response.data;
}

export async function sendCode(phone: string, tenantId?: string): Promise<SendCodeResult> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  try {
    const response = await apiClient.post<SendCodeResult>(
      '/max-client/send-code',
      { phone },
      { headers, params }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.type) {
      throw error.response.data as PhoneAuthError;
    }
    throw error;
  }
}

export async function verifyCode(
  phone: string,
  code: string,
  tenantId?: string
): Promise<VerifyCodeResult> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  try {
    const response = await apiClient.post<VerifyCodeResult>(
      '/max-client/verify-code',
      { phone, code },
      { headers, params }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.type) {
      throw error.response.data as PhoneAuthError;
    }
    throw error;
  }
}
